import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { FileText, Building2, Scale, Shield, Link, AlertTriangle, Globe, CreditCard } from 'lucide-react';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg text-white">
          <FileText className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">Terms & Conditions</h2>
          <p className="text-sm text-slate-400">Last updated on Dec 17th 2025</p>
        </div>
      </div>

      <Card className="border-slate-700 bg-slate-800/50 shadow-md">
        <CardContent className="p-6">
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6 text-slate-300">
              {/* Introduction */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-indigo-600">
                  <Building2 className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Introduction</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  For the purpose of these Terms and Conditions, The term &quot;we&quot;, &quot;us&quot;, &quot;our&quot; used anywhere on this page shall mean <strong>MYONK</strong>, whose registered/operational office is:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg text-sm">
                  <p className="font-medium text-gray-900 dark:text-foreground">MYONK</p>
                  <p>10/A, 2nd Floor, 3rd Cross</p>
                  <p>Someshwara Layout, Yelahanka</p>
                  <p>Bangalore North, Karnataka 560064</p>
                  <p>India</p>
                </div>
                <p className="text-sm leading-relaxed">
                  &quot;you&quot;, &quot;your&quot;, &quot;user&quot;, &quot;visitor&quot; shall mean any natural or legal person who is visiting our website and/or agreed to purchase from us.
                </p>
                <p className="text-sm leading-relaxed font-medium text-gray-800">
                  Your use of the website and/or purchase from us are governed by the following Terms and Conditions:
                </p>
              </section>

              {/* Content Changes */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-orange-600">
                  <AlertTriangle className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Content Changes</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  The content of the pages of this website is subject to change without notice.
                </p>
              </section>

              {/* Disclaimer */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-red-600">
                  <Shield className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Disclaimer & Liability</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  Neither we nor any third parties provide any warranty or guarantee as to the accuracy, timeliness, performance, completeness or suitability of the information and materials found or offered on this website for any particular purpose. You acknowledge that such information and materials may contain inaccuracies or errors and we expressly exclude liability for any such inaccuracies or errors to the fullest extent permitted by law.
                </p>
                <p className="text-sm leading-relaxed">
                  Your use of any information or materials on our website and/or product pages is entirely at your own risk, for which we shall not be liable. It shall be your own responsibility to ensure that any products, services or information available through our website and/or product pages meet your specific requirements.
                </p>
              </section>

              {/* Intellectual Property */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-purple-600">
                  <Scale className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Intellectual Property</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  Our website contains material which is owned by or licensed to us. This material includes, but is not limited to, the design, layout, look, appearance and graphics. Reproduction is prohibited other than in accordance with the copyright notice, which forms part of these terms and conditions.
                </p>
                <p className="text-sm leading-relaxed">
                  All trademarks reproduced in our website which are not the property of, or licensed to, the operator are acknowledged on the website.
                </p>
                <p className="text-sm leading-relaxed">
                  Unauthorized use of information provided by us shall give rise to a claim for damages and/or be a criminal offense.
                </p>
              </section>

              {/* External Links */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-blue-600">
                  <Link className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">External Links</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  From time to time our website may also include links to other websites. These links are provided for your convenience to provide further information.
                </p>
                <p className="text-sm leading-relaxed">
                  You may not create a link to our website from another website or document without <strong>MYONK&apos;s</strong> prior written consent.
                </p>
              </section>

              {/* Governing Law */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-teal-600">
                  <Globe className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Governing Law</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  Any dispute arising out of use of our website and/or purchase with us and/or any engagement with us is subject to the laws of <strong>India</strong>.
                </p>
              </section>

              {/* Payment Terms */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-green-600">
                  <CreditCard className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Payment & Transaction Terms</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  We shall be under no liability whatsoever in respect of any loss or damage arising directly or indirectly out of the decline of authorization for any Transaction, on Account of the Cardholder having exceeded the preset limit mutually agreed by us with our acquiring bank from time to time.
                </p>
              </section>

              {/* Footer */}
              <div className="pt-6 border-t border-slate-600">
                <p className="text-xs text-slate-400 text-center">
                  © 2026 MYONK. All rights reserved.
                </p>
                <p className="text-xs text-slate-500 text-center mt-1">
                  By using MyVenueMate, you agree to these Terms and Conditions.
                </p>
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
      
      {/* Back to Home */}
      <div className="text-center pt-4">
        <button 
          onClick={() => window.location.href = '/'}
          className="px-4 py-2 border border-slate-600 text-slate-300 rounded-md hover:bg-slate-700/50 transition-colors"
        >
          Back to Home
        </button>
      </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
